'use client'

import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faExclamationTriangle, 
  faCheckCircle, 
  faInfoCircle, 
  faExclamationCircle 
} from '@fortawesome/free-solid-svg-icons'

export type MessageType = 'error' | 'success' | 'warning' | 'info'

export interface MessageProps {
  children: React.ReactNode
  type?: MessageType
  timeout?: number
  afterTimeout?: () => void
}

const Message: React.FC<MessageProps> = ({ children, type = 'error', timeout = 0, afterTimeout }) => {
  const [displayChildren, setDisplayChildren] = useState<React.ReactNode>(null)
  const [show, setShow] = useState(false)

  // Get styling and icon based on message type
  const getMessageStyles = (messageType: MessageType) => {
    switch (messageType) {
      case 'success':
        return {
          classes: 'text-green-800 border-green-600 bg-green-50 dark:bg-gray-800 dark:text-green-400 dark:border-green-400',
          icon: faCheckCircle,
          progressColor: 'bg-green-400'
        }
      case 'warning':
        return {
          classes: 'text-yellow-800 border-yellow-600 bg-yellow-50 dark:bg-gray-800 dark:text-yellow-400 dark:border-yellow-400',
          icon: faExclamationTriangle,
          progressColor: 'bg-yellow-400'
        }
      case 'info':
        return {
          classes: 'text-blue-800 border-blue-600 bg-blue-50 dark:bg-gray-800 dark:text-blue-400 dark:border-blue-400',
          icon: faInfoCircle,
          progressColor: 'bg-blue-400'
        }
      case 'error':
      default:
        return {
          classes: 'text-red-800 border-red-600 bg-red-50 dark:bg-gray-800 dark:text-red-400 dark:border-red-400',
          icon: faExclamationCircle,
          progressColor: 'bg-red-400'
        }
    }
  }

  const messageStyles = getMessageStyles(type)

  useEffect(() => {
    if (children) {
      setDisplayChildren(children)
      const showTimer = setTimeout(() => setShow(true), 10)
      let hideTimer: NodeJS.Timeout | null = null
      if (timeout > 0) {
        hideTimer = setTimeout(() => {
          setShow(false)
          if (afterTimeout) {
            afterTimeout()
          }
        }, timeout)
      }

      return () => {
        clearTimeout(showTimer)
        if (hideTimer) {
          clearTimeout(hideTimer)
        }
      }
    } else {
      setShow(false)
    }
  }, [children, timeout, afterTimeout])

  if (!displayChildren) {
    return null
  }

  return (
    <div
      className={`relative overflow-hidden transition-all duration-700 ease-in-out ${
        show ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
      } flex items-center p-4 mb-4 text-sm border rounded-lg ${messageStyles.classes}`}
      role="alert"
      onTransitionEnd={() => {
        if (!show) {
          setDisplayChildren(null)
        }
      }}
    >
      <FontAwesomeIcon icon={messageStyles.icon} className="flex-shrink-0 inline w-4 h-4 mr-3" />
      <span className="font-medium">{displayChildren}</span>
      {show && timeout > 0 && (
        <div
          className={`absolute bottom-0 left-0 h-1 ${messageStyles.progressColor} animate-progress-bar`}
          style={{ animationDuration: `${Math.floor(timeout / 1000)}s` }}
        ></div>
      )}
    </div>
  )
}

export default Message
